import React, { useState, useCallback, useEffect } from 'react';
import { FaSearch, FaPlus, FaTrash } from 'react-icons/fa';
import { RiFileExcel2Line } from "react-icons/ri";
import './Table.css';

const Table = ({
  className,
  title,
  columns,
  data,
  page,
  paging,
  onRowClick,
  renderExpandedRow,
  onAddClick,
  showHeader = true,
  getRowId = (row) =>{ return row.id},
  onDataChange,
  showSearchBar = true,
  showCheckbox,
  onDeleteClick,
  excelImport = null,
  excelExport = null,
  sortConfig = null,
  onSort = null,
  emptyState = null,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [localData, setLocalData] = useState([]);
  const [expandedRows, setExpandedRows] = useState([]);

  useEffect(() => {
    if (Array.isArray(data)) {
      setLocalData(data);
      setFilteredData(data);
    }
  }, [data]);

  const handleSearch = useCallback((term) => {
    setSearchTerm(term);
    const filtered = localData.filter(row =>
      columns.some(column =>
        String(row[column.accessor]).toLowerCase().includes(term.toLowerCase())
      )
    );
    setFilteredData(filtered);
  }, [localData, columns]);

  const handleCheckboxChange = useCallback((row, isChecked) => {
    const updatedData = localData.map(item =>
      item === row ? { ...item, checked: isChecked } : item
    );
    setLocalData(updatedData);
    setFilteredData(updatedData.filter(row =>
      columns.some(column =>
        String(row[column.accessor]).toLowerCase().includes(searchTerm.toLowerCase())
      )
    ));
    onDataChange && onDataChange(updatedData);
  }, [localData, columns, searchTerm, onDataChange]);

  const handleSelectAll = useCallback((isChecked) => {
    const updatedData = localData.map(item => ({ ...item, checked: isChecked }));
    setLocalData(updatedData);
    setFilteredData(updatedData.filter(row =>
      columns.some(column =>
        String(row[column.accessor]).toLowerCase().includes(searchTerm.toLowerCase())
      )
    ));
    onDataChange && onDataChange(updatedData);
  }, [localData, columns, searchTerm, onDataChange]);


  const                                                                                                                                handleRowToggle = (row) => {
    const rowKey = getRowId(row);
    setExpandedRows((prev) =>
      prev.includes(rowKey) ? prev.filter((id) => id !== rowKey) : [...prev, rowKey]
    );
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= page.totalPage) {
      paging(newPage);
    }
  }; 

  // 페이지네이션 관련
  const totalPages = page?.totalPage ?? 1;
  const currentPage = page?.page ?? 1;
  const pageSize = 10;
  const startPage = Math.floor((currentPage - 1) / pageSize) * pageSize + 1;
  const endPage = Math.min(startPage + pageSize - 1, totalPages);
  const pageNumbers = [];
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  return (
    <div className={showHeader ? "table-container" : ""}>
      {showHeader &&
      <div className="table-header-container">
        <div className="table-title-area">
          {title && <div className="table-title">{title}</div>}
          {page?.totalSize != null && (
            <span className="table-count">
              전체 {page.totalSize.toLocaleString()}건
              {page.totalPage > 1 && ` | ${page.page} / ${page.totalPage} 페이지`}
            </span>
          )}
        </div>
        <div className="controls">
          {showSearchBar && (
            <div className="search-bar">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
          )}
          <div className="table-buttons">
            {excelExport && (
              <div className="excel-button" onClick={excelExport}>
                <RiFileExcel2Line className="button-icon" />내보내기
              </div>
            )}
            {excelImport && (
              <div className="excel-button" onClick={excelImport}>
                <RiFileExcel2Line className="button-icon" />가져오기
              </div>
            )}
            {onAddClick && (
              <button className="add-button" onClick={onAddClick}>
                <FaPlus className="button-icon" /> 등록
              </button>
            )}
            {onDeleteClick && (
              <button className="delete-button" onClick={onDeleteClick}>
                <FaTrash className="button-icon" /> 삭제
              </button>
            )}
          </div>
        </div>
      </div>}

      <div className="table-body-container">
        <table className={`custom-table ${className || ''}`}>
          <thead>
            <tr className="table-header">
              {showCheckbox && (
                <th>
                  <input
                    type="checkbox"
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    checked={localData.length > 0 && localData.every(item => item.checked)}
                  />
                </th>
              )}
              {columns.map((column, index) => {
                const isSortable = onSort && column.sortable !== false;
                const isActive = sortConfig && sortConfig.key === column.accessor;
                return (
                  <th
                    key={index}
                    className={`header-cell${isSortable ? ' sortable' : ''}${isActive ? ' sort-active' : ''}`}
                    style={column.align ? { textAlign: column.align } : undefined}
                    onClick={isSortable ? () => onSort(column.accessor) : undefined}
                  >
                    {column.header}
                    {isActive && (
                      <span className="sort-indicator">
                        {sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}
                      </span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {Array.isArray(filteredData) && filteredData.length > 0 ? (
              filteredData.map((row, rowIndex) => (
                <React.Fragment key={rowIndex}>
                  <tr
                    onClick={() => {
                      onRowClick && onRowClick(row);
                      handleRowToggle(row);
                    }}
                    className={row.checked ? 'checked-row' : ''}
                  >
                    {showCheckbox && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={row.checked || false}
                          onChange={(e) => handleCheckboxChange(row, e.target.checked)}
                        />
                      </td>
                    )}
                    {columns.map((column, colIndex) => (
                      <td
                        key={colIndex}
                        style={column.align ? { textAlign: column.align } : undefined}
                      >
                        {column.render ? column.render(row) : row[column.accessor]}
                      </td>
                    ))}
                  </tr>

                  {expandedRows.includes(getRowId(row)) && renderExpandedRow && (
                    <tr className="expanded-row">
                      <td colSpan={columns.length + (showCheckbox ? 1 : 0)}>
                        {renderExpandedRow(row)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length + (showCheckbox ? 1 : 0)}>
                  {emptyState || '데이터가 없습니다.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {page && totalPages > 1 && (
        <div className="pagination">
          <button
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            {"<"}
          </button>
          {pageNumbers.map((pageNum) => (
            <button
              key={pageNum}
              onClick={() => handlePageChange(pageNum)}
              className={currentPage === pageNum ? "active" : ""}
            >
              {pageNum}
            </button>
          ))}
          <button
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            {">"}
          </button>
        </div>
      )}
    </div>
  );
};

export default Table;
